import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Folder, MoreVertical, Printer, Share2, Trash2 } from 'lucide-react';
import type { FolderDelivery, HistoryEntry } from '@worki/shared';
import { api, authedFetch, RequestError } from '../../lib/api.js';
import { currentLocale } from '../../i18n.js';
import { formatDateTime } from '../../lib/format.js';
import { Modal } from '../Modal.js';
import { PdfPreview } from '../PdfPreview.js';
import { AnchoredMenu } from '../AnchoredMenu.js';
import { useToast } from '../Toast.js';
import { browserShareHost, shareFile } from '../../pages/library/shareDocument.js';

export type FolderHistoryEntry = Extract<HistoryEntry, { delivery: FolderDelivery }>;
export const folderDeliveryPath = (entry: FolderHistoryEntry) => `/history/${entry.kind}/${encodeURIComponent(entry.delivery.id)}`;
export const folderDeliveryKey = (entry: FolderHistoryEntry) => `${entry.kind}:${entry.delivery.id}`;

/** Delivery channels are recorded facts: unknown values must not be inferred from contact details. */
export function channelTranslationKey(channel?: string | null): string {
  return channel && ['email', 'whatsapp', 'both', 'copy'].includes(channel) ? `history.channel.${channel}` : 'history.channelUnknown';
}

/** Unknown delivery metadata is omitted, never guessed from recipient details. */
export function RecordedDeliveryChannel({channel}:{channel?:string|null}) {
  const {t}=useTranslation();
  const key=channelTranslationKey(channel);
  return key==='history.channelUnknown'?null:<span className="block text-[11px] text-muted-foreground">{t(key)}</span>;
}

export async function deleteFolderSelections(keys: string[]): Promise<{ ok: number; failed: string[] }> {
  const results = await Promise.all(keys.map(async key => {
    const separator = key.indexOf(':');
    try { await api.del(`/history/${key.slice(0, separator)}/${encodeURIComponent(key.slice(separator + 1))}`); return null; }
    catch { return key; }
  }));
  const failed = results.filter((key): key is string => key !== null);
  return { ok: keys.length - failed.length, failed };
}

function useSavedPdfActions(entry: FolderHistoryEntry) {
  const { t } = useTranslation();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const path = folderDeliveryPath(entry) + '/pdf';
  const filename = `${entry.delivery.title.replace(/[\\/:*?"<>|]/g, '_') || 'shipment'}.pdf`;
  const run = async (action: 'share' | 'print') => {
    if (busy) return;
    setBusy(true);
    try {
      if (action === 'print') await api.openFile(path, filename);
      else {
        const response = await authedFetch(path);
        if (!response.ok) throw new RequestError(response.status, response.status === 410 ? 'history.folderOriginalUnavailable' : 'errors.unknown');
        await shareFile(new File([await response.blob()], filename, { type: 'application/pdf' }), entry.delivery.title, browserShareHost());
      }
    } catch (error) { toast.error(t(error instanceof RequestError ? error.code : 'errors.unknown')); }
    finally { setBusy(false); }
  };
  return { busy, path, filename, run };
}

export function FolderDeliveryRow({ entry, number, selectMode, selected, onToggleSelect, onOpen, onDelete, onPressStart, onPressEnd, swallowClick }: {
  entry: FolderHistoryEntry; number: number; selectMode: boolean; selected: boolean;
  onToggleSelect: () => void; onOpen: () => void; onDelete: () => void;
  onPressStart: () => void; onPressEnd: () => void; swallowClick: () => boolean;
}) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  const pdf = useSavedPdfActions(entry);
  const activate = () => { if (!swallowClick()) { if (selectMode) onToggleSelect(); else onOpen(); } };
  return <div role="button" tabIndex={0} className="glass-card list-tint rounded-lg p-3 cursor-pointer space-y-1"
    onClick={activate} onKeyDown={event => { if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); activate(); } }}
    onMouseDown={onPressStart} onMouseUp={onPressEnd} onMouseLeave={onPressEnd}
    onTouchStart={onPressStart} onTouchEnd={onPressEnd} onTouchMove={onPressEnd} onTouchCancel={onPressEnd}>
    <div className="flex items-center gap-2">
      {selectMode && <input type="checkbox" checked={selected} onChange={onToggleSelect} onClick={event => event.stopPropagation()} aria-label={t('common.selectRow')} className="h-4 w-4 accent-primary" />}
      <Folder className="h-4 w-4 shrink-0 text-primary" />
      <span className="text-xs text-muted-foreground ltr-nums" aria-label={t('history.resultNumber', { number })}>{number}.</span>
      <p className="text-sm font-semibold flex-1 truncate">{entry.delivery.title}</p>
      <span onClick={event => event.stopPropagation()} onMouseDown={event => event.stopPropagation()} onTouchStart={event => event.stopPropagation()}>
        <button type="button" ref={anchor} onClick={() => setMenu(!menu)} aria-label={t('common.actions')} aria-expanded={menu} className="p-1.5 rounded-lg hover:bg-muted"><MoreVertical className="h-4 w-4" /></button>
        <AnchoredMenu open={menu} onClose={() => setMenu(false)} anchorRef={anchor} width={220} align="end">
          <button type="button" disabled={pdf.busy} onClick={() => { setMenu(false); void pdf.run('share'); }} className="w-full px-3 py-2 flex gap-2 text-sm text-start"><Share2 className="h-4 w-4" />{t('common.share')}</button>
          <button type="button" disabled={pdf.busy} onClick={() => { setMenu(false); void pdf.run('print'); }} className="w-full px-3 py-2 flex gap-2 text-sm text-start"><Printer className="h-4 w-4" />{t('history.openForPrint')}</button>
          <button type="button" onClick={() => { setMenu(false); onDelete(); }} className="w-full px-3 py-2 flex gap-2 text-sm text-destructive text-start"><Trash2 className="h-4 w-4" />{t('common.delete')}</button>
        </AnchoredMenu>
      </span>
    </div>
    <p className="text-xs text-muted-foreground">{t(`history.kind.${entry.kind}`)} · {entry.delivery.documentCount} · {entry.delivery.format}</p>
    <p className="text-sm" dir="auto">{entry.delivery.recipient ?? '—'}</p>
    <p className="text-xs text-muted-foreground">{formatDateTime(entry.delivery.sentAt, currentLocale())}<RecordedDeliveryChannel channel={entry.delivery.channel}/></p>
  </div>;
}

export function FolderDeliveryPreview({ entry, onClose }: { entry: FolderHistoryEntry; onClose: () => void }) {
  const { t } = useTranslation();
  const pdf = useSavedPdfActions(entry);
  const preview = useQuery({ queryKey: ['history', 'preview', entry.kind, entry.delivery.id], queryFn: () => api.get<{ delivery: FolderDelivery; files: { id: string; name: string }[]; pdfAvailable: boolean; manifestAvailable?: boolean }>(folderDeliveryPath(entry) + '/preview'), retry: false });
  const [showCurrent,setShowCurrent]=useState(false);
  const current=useQuery({queryKey:['history','current-folder',entry.kind,entry.delivery.id],queryFn:()=>api.get<{basis:'current';files:{id:string;name:string}[];pdfAvailable:boolean}>(folderDeliveryPath(entry)+'/current-folder'),enabled:showCurrent,retry:false});
  const delivery = preview.data?.delivery ?? entry.delivery;
  return <Modal open title={t('history.folderPreviewTitle')} onClose={onClose} size="full">
    <div className="space-y-3">
      <h3 className="font-semibold" dir="auto">{delivery.title}</h3>
      <p className="text-sm" dir="auto">{delivery.recipient ?? '—'}</p>
      <p className="text-xs text-muted-foreground">{formatDateTime(delivery.sentAt, currentLocale())}<RecordedDeliveryChannel channel={delivery.channel}/>{delivery.format}</p>
      {preview.isPending && <p role="status">{t('common.loading')}</p>}
      {preview.isError && <p role="alert">{t(preview.error instanceof RequestError ? preview.error.code : 'errors.unknown')}</p>}
      {preview.data && <>
        <h4 className="text-sm font-medium">{t('history.folderFiles', { count: preview.data.manifestAvailable || preview.data.files.length > 0 ? preview.data.files.length : delivery.documentCount })}</h4>
        {preview.data.files.length ? <ol className="list-decimal ps-6 text-sm space-y-1">{preview.data.files.map((file, index) => <li key={`${file.id}:${index}`} dir="auto">{file.name}</li>)}</ol> : <p className="text-sm text-muted-foreground">{t('history.folderManifestUnavailable')}</p>}
        {preview.data.pdfAvailable ? <>
          <PdfPreview src={pdf.path} fallbackPath={pdf.path} fallbackName={pdf.filename} />
          <div className="flex flex-wrap gap-2"><button type="button" disabled={pdf.busy} onClick={() => void pdf.run('share')} className="rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground">{t('common.share')}</button><button type="button" disabled={pdf.busy} onClick={() => void pdf.run('print')} className="rounded-lg bg-muted px-3 py-2 text-sm">{t('history.openForPrint')}</button></div>
        </> : <p role="status" className="rounded-lg bg-muted p-3 text-sm">{t('history.folderOriginalUnavailable')}</p>}
        {!preview.data.pdfAvailable && delivery.folderId && <button type="button" className="rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground" onClick={()=>setShowCurrent(true)}>{t('history.viewCurrentFolder')}</button>}
        {showCurrent && <section className="space-y-3 border-t border-border pt-3">
          <p className="text-sm font-medium">{t('history.currentFolderNotice')}</p>
          {current.isPending && <p role="status">{t('common.loading')}</p>}
          {current.isError && <p role="alert">{t(current.error instanceof RequestError ? current.error.code : 'errors.unknown')}</p>}
          {current.data && <>
            <h4 className="text-sm">{t('history.currentFolderFiles',{count:current.data.files.length})}</h4>
            <ol className="list-decimal ps-6 text-sm">{current.data.files.map(file=><li key={file.id} dir="auto">{file.name}</li>)}</ol>
            {current.data.pdfAvailable ? <PdfPreview src={folderDeliveryPath(entry)+'/current-folder/pdf'} fallbackPath={folderDeliveryPath(entry)+'/current-folder/pdf'} fallbackName="current-folder.pdf" /> : <p role="status">{t('history.currentFolderEmpty')}</p>}
          </>}
        </section>}
      </>}
    </div>
  </Modal>;
}
